import { RoutineResponse } from '~/common/entities/Routines/RoutineResponse';
import { RoutineRequest } from '~/common/entities/Routines/RoutineRequest';
import { useAuthenticationService } from './AuthenticationService';
import { Routine } from '~/common/entities/Routines/RoutineTypes';
import { ApiErrorCallback, ApiSuccessCallback } from './callback';
import { authenticatedApi } from '../utils/authenticatedApi';
import { momentDurationToSeconds } from '../utils/timeUtils';
import { createContainer } from 'unstated-next';
import { useState } from 'react';
import _ from 'lodash';

export interface EditRoutineValues {
  name: string;
  durationHours: number;
  durationMinutes: number;
  durationSeconds: number;
  overallDuration?: number;
  description: string;
  employees: string[];
}

const initialData = [] as Routine[];

function useRoutine() {
  const [routines, setRoutines] = useState(initialData);
  const { authenticationState } = useAuthenticationService();

  const setRoutinesOrdered = (routines: Routine[]) => {
    setRoutines(
      _.sortBy(routines, (routine) => routine.name.toLocaleLowerCase()),
    );
  };

  const createRoutine = async (
    values: RoutineRequest,
    onSuccess?: ApiSuccessCallback<RoutineResponse>,
    onError?: ApiErrorCallback,
  ) => {
    const { data, error: ApiError } = await authenticatedApi(
      authenticationState,
    ).post<RoutineResponse, RoutineRequest>('/routine/', values);
    if (ApiError && onError) onError(ApiError);
    if (data) {
      setRoutinesOrdered([...routines, data]);
    }
    if (data && onSuccess) onSuccess(data);
  };

  const editRoutine = async (
    newRoutineValues: EditRoutineValues,
    oldRoutine: Routine,
    onSuccess?: ApiSuccessCallback<RoutineResponse>,
    onError?: ApiErrorCallback,
  ) => {
    let duration = 0;

    if (newRoutineValues.overallDuration) {
      duration = momentDurationToSeconds(newRoutineValues.overallDuration);
    } else {
      duration =
        newRoutineValues.durationHours * 60 * 60 +
        newRoutineValues.durationMinutes * 60 +
        newRoutineValues.durationSeconds;
    }

    const companyString = '' + oldRoutine.company;
    const values: RoutineRequest = {
      name: newRoutineValues.name,
      duration: duration,
      description: newRoutineValues.description,
      employees: newRoutineValues.employees || [],
      company: companyString,
    };
    const { data, error: ApiError } = await authenticatedApi(
      authenticationState,
    ).put<RoutineResponse, RoutineRequest>(
      `/routine/${oldRoutine.id}/`,
      values,
    );

    if (ApiError && onError) onError();
    if (data) {
      setRoutinesOrdered([...routines, data]);
    }
    if (data && onSuccess) onSuccess(data);
  };

  const deleteRoutine = async (
    routine: Routine,
    onError?: ApiErrorCallback,
  ) => {
    const { error } = await authenticatedApi(authenticationState).delete<{}>(
      `/routine/${routine.id}/`,
    );
    if (error && onError) onError();
    if (!error) {
      setRoutinesOrdered(routines.filter((p) => p.id !== routine.id));
    }
  };

  const getRoutineFromServer = async (
    routineId: string,
    onSuccess?: ApiSuccessCallback<Routine>,
    onError?: ApiErrorCallback,
  ) => {
    const { data, error } = await authenticatedApi(
      authenticationState,
    ).get<Routine>(`/routine/${routineId}/`);
    if (error) {
      return onError ? onError(error) : undefined;
    }
    if (onSuccess) {
      onSuccess(data);
    }
    return data;
  };

  const addEmployee = async (
    routine: Routine,
    employeeId: string,
    onSuccess?: ApiSuccessCallback<Routine>,
    onError?: ApiErrorCallback,
  ) => {
    const routineEmployeeIds = routine.employees.map((e) => e.id);
    const employees = [...routineEmployeeIds, employeeId];
    const newRoutineValues: EditRoutineValues = {
      name: routine.name,
      durationHours: 0,
      durationMinutes: 0,
      durationSeconds: 0,
      overallDuration: routine.duration,
      description: routine.description,
      employees,
    };
    await editRoutine(newRoutineValues, routine, onSuccess, onError);
  };

  const fillStoreWithServerData = async (
    onSuccess?: ApiSuccessCallback<Routine[]>,
    onError?: ApiErrorCallback,
  ) => {
    const { data, error } =
      await authenticatedApi(authenticationState).get<Routine[]>('/routines/');
    if (error) return onError ? onError(error) : undefined;
    if (data.length > 0) setRoutinesOrdered(data);
    if (data && onSuccess) onSuccess(data);
  };

  return {
    routines,
    setRoutines,
    createRoutine,
    editRoutine,
    deleteRoutine,
    getRoutineFromServer,
    addEmployee,
    fillStoreWithServerData,
  };
}

export const { Provider: RoutineProvider, useContainer: useRoutineService } =
  createContainer(useRoutine);
